export class AudioFileError extends Error {
  reason: "too_large" | "empty" | "unreadable";

  constructor(reason: AudioFileError["reason"]) {
    super(`Audio file preparation failed: ${reason}`);
    this.name = "AudioFileError";
    this.reason = reason;
  }
}

export function prepareAudioFile(
  file: File,
  maxBytes: number,
  copyToMemory: boolean,
  signal: AbortSignal,
): Promise<File> {
  if (signal.aborted) return Promise.reject(new DOMException("File selection cancelled", "AbortError"));
  if (!copyToMemory) {
    if (file.size > maxBytes) return Promise.reject(new AudioFileError("too_large"));
    if (!file.size) return Promise.reject(new AudioFileError("empty"));
    return Promise.resolve(file);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const cleanup = () => {
      reader.onload = reader.onerror = reader.onabort = null;
      signal.removeEventListener("abort", cancel);
    };
    const fail = (error: Error) => {
      cleanup();
      reject(error);
      if (reader.readyState === FileReader.LOADING) reader.abort();
    };
    const cancel = () => fail(new DOMException("File selection cancelled", "AbortError"));
    reader.onerror = () => fail(new AudioFileError("unreadable"));
    reader.onabort = cancel;
    reader.onload = () => {
      const bytes = reader.result;
      if (!(bytes instanceof ArrayBuffer)) {
        fail(new AudioFileError("unreadable"));
      } else if (bytes.byteLength > maxBytes) {
        fail(new AudioFileError("too_large"));
      } else if (!bytes.byteLength) {
        fail(new AudioFileError("empty"));
      } else {
        cleanup();
        resolve(new File([bytes], file.name, { type: file.type }));
      }
    };
    signal.addEventListener("abort", cancel, { once: true });
    try {
      // Android file providers can invalidate a File when size is inspected
      // before reading starts. Start in the selection event, then upload a copy.
      reader.readAsArrayBuffer(file);
      if (file.size > maxBytes) fail(new AudioFileError("too_large"));
    } catch {
      fail(new AudioFileError("unreadable"));
    }
  });
}
